// filepath: src/utils/marriageProfileSectionEditUtils.ts

import type {
  AstroState,
  FamilyState,
  LifestyleState,
  MarriageProfileSection,
  PersonalState,
  ProfessionalState,
  SectionState,
  SummaryState,
} from "src/types/marriageProfileSectionEdit.types";

export const sectionTitle: Record<MarriageProfileSection, string> = {
  SUMMARY: "Edit Profile",
  PERSONAL: "Edit Personal Information",
  PROFESSIONAL: "Edit Professional Details",
  FAMILY: "Edit Family Background",
  LIFESTYLE: "Edit Lifestyle",
  ASTRO: "Edit Astro Details",
};

export function buildBackendPatch(section: MarriageProfileSection, patch: SectionState) {
  // Map section state keys -> backend DTO keys expected by PATCH.
  // Keep this mapping minimal and only include keys that exist in patch.
  const out: Record<string, any> = {};

  if (section === "SUMMARY") {
    const p = patch as SummaryState;
    if (p.fullName !== undefined) out.fullName = p.fullName;
    if (p.about !== undefined) {
      const trimmed = String(p.about || "").trim();
      out.about = trimmed ? trimmed : null;
    }
  }

  if (section === "PERSONAL") {
    const p = patch as PersonalState;
    if (p.dob !== undefined) out.dateOfBirth = p.dob;
    if (p.maritalStatus !== undefined) out.maritalStatus = p.maritalStatus;
    if (p.gender !== undefined) out.gender = p.gender;
    if (p.casteDetails !== undefined) out.casteDetails = p.casteDetails;
    if (p.timeOfBirth !== undefined) {
      const v = p.timeOfBirth;
      const trimmed = v == null ? "" : String(v).trim();
      out.timeOfBirth = trimmed ? trimmed : null;
    }
    if (p.religion !== undefined) out.religion = p.religion || "HINDU";
  }

  if (section === "PROFESSIONAL") {
    const p = patch as ProfessionalState;
    if (p.highestEducation !== undefined) out.highestEducation = p.highestEducation;
    if (p.collegeOrUniversity !== undefined) out.collegeOrUniversity = p.collegeOrUniversity;
    if (p.employmentSector !== undefined) out.employmentSector = p.employmentSector;
    if (p.designation !== undefined) out.designation = p.designation;
    if (p.employerName !== undefined) out.employerName = p.employerName;
    if (p.annualIncome !== undefined) {
      const trimmed = String(p.annualIncome || "").trim();
      out.annualIncome = trimmed ? Number(trimmed) : null;
    }
    if (p.socialMediaLinksList !== undefined) {
      const raw = Array.isArray(p.socialMediaLinksList) ? p.socialMediaLinksList : [];
      const cleaned = raw
        .map((x: any) => {
          const socialMediaPlatforms = String(x?.socialMediaPlatforms ?? x?.platform ?? "").trim();
          const link = String(x?.link ?? x?.url ?? "").trim();
          const id = x?.id;
          return {
            ...(id != null && id !== "" ? { id } : {}),
            socialMediaPlatforms,
            link,
          };
        })
        .filter((x: any) => x.socialMediaPlatforms && x.link);

      // IMPORTANT:
      // - If user edited links but removed/cleared them all, do NOT send an empty array.
      //   Sending [] clears previously saved links server-side.
      // - Only send when there is at least one valid entry.
      if (cleaned.length > 0) {
        out.socialMediaLinksList = cleaned;
      }
    }
  }

  if (section === "FAMILY") {
    const p = patch as FamilyState;
    if (p.fatherName !== undefined) out.fatherName = p.fatherName;
    if (p.motherName !== undefined) out.motherName = p.motherName;
    if (p.fatherOccupation !== undefined) out.fatherOccupation = p.fatherOccupation;
    if (p.motherOccupation !== undefined) out.motherOccupation = p.motherOccupation;
    if (p.familyType !== undefined) out.familyType = p.familyType;
    if (p.familyStatus !== undefined) out.familyStatus = p.familyStatus;
    if (p.nativePlace !== undefined) out.nativePlace = p.nativePlace;
    if (p.totalFamilyMembers !== undefined) {
      const trimmed = String(p.totalFamilyMembers || "").trim();
      out.totalFamilyMembers = trimmed ? Number(trimmed) : null;
    }
  }

  if (section === "LIFESTYLE") {
    const p = patch as LifestyleState;
    if (p.heightCm !== undefined) {
      const trimmed = String(p.heightCm || "").trim();
      out.heightInCm = trimmed ? Number(trimmed) : null;
    }
    if (p.bodyType !== undefined) out.bodyType = p.bodyType;
    if (p.skinTone !== undefined) out.skinTone = p.skinTone;

    // enums
    if (p.smokingHabit !== undefined) {
      const trimmed = String(p.smokingHabit || "").trim();
      out.smokingHabit = trimmed ? trimmed : null;
    }
    if (p.drinkingHabit !== undefined) {
      const trimmed = String(p.drinkingHabit || "").trim();
      out.drinkingHabit = trimmed ? trimmed : null;
    }

    if (p.dietaryHabits !== undefined) out.dietaryHabits = p.dietaryHabits;
    if (p.hobbies !== undefined) out.hobbiesInterests = p.hobbies;
    if (p.ownsHouse !== undefined) {
      const trimmed = String(p.ownsHouse || "").trim();
      out.ownsHouse = trimmed === "true" ? true : trimmed === "false" ? false : null;
    }
  }

  if (section === "ASTRO") {
    const p = patch as AstroState;
    if (p.nakshatra !== undefined) out.nakshatra = p.nakshatra;
    if (p.rasi !== undefined) out.rasi = p.rasi;
    if (p.gothram !== undefined) out.gothram = p.gothram;
    if (p.dosham !== undefined) out.dosham = p.dosham;
    if ((p as any).timeOfBirth !== undefined) {
      const trimmed = String((p as any).timeOfBirth || "").trim();
      out.timeOfBirth = trimmed ? trimmed : null;
    }
  }

  return out;
}

export function validateSection(
  section: MarriageProfileSection,
  values: SectionState
): Record<string, string> {
  const next: Record<string, string> = {};

  if (section === "SUMMARY") {
    const v = values as SummaryState;
    if (!String(v.fullName || "").trim()) next.fullName = "Full name is required";
  }

  if (section === "PERSONAL") {
    const v = values as PersonalState;
    if (!String(v.dob || "").trim()) {
      next.dob = "Date of birth is required";
    } else {
      const dobDate = new Date(String(v.dob).trim());
      if (!Number.isNaN(dobDate.getTime()) && dobDate > new Date()) {
        next.dob = "Date of birth cannot be a future date";
      }
    }
    if (!String(v.maritalStatus || "").trim()) next.maritalStatus = "Marital status is required";
    if (!String(v.gender || "").trim()) next.gender = "Gender is required";
  }

  return next;
}
